// ─── Play Cell ────────────────────────────────────────────────
// Per-symbol order execution controls:
//   - Condition price (>= 0)
//   - Max orders (>= 0, default 0)
//   - Off button: OFF → Buy/Sell → BUY/SELL → OFF (direction toggle)
//   - Order Now button: ORDER → Buy/Sell → BUY/SELL → OFF
//   Two independent toggles with identical UI, different purpose.
//
// State is lifted to ChinMonitor for workspace persistence.
// Risk control gates the Order Now button via riskBlocked/riskReason props.

import { useState, useRef, useEffect } from 'react';
import type { OrderLoopStatus, OrderLoopPhase } from '@/lib/chin-order-executor';
import type { OffEngineStatus, OffEnginePhase } from '@/lib/chin-off-engine';

interface PlayCellProps {
  symbolId: number;
  conditionPrice: string;
  maxOrders: string;
  direction: 'buy' | 'sell' | null;
  orderDirection: 'buy' | 'sell' | null;
  onPriceChange: (symbolId: number, val: string) => void;
  onMaxChange: (symbolId: number, val: string) => void;
  onDirectionChange: (symbolId: number, dir: 'buy' | 'sell' | null) => void;
  onOrderDirectionChange: (symbolId: number, dir: 'buy' | 'sell' | null) => void;
  /** Volume (lots) for order engine */
  orderVolume: string;
  onVolumeChange: (symbolId: number, val: string) => void;
  /** Reset all play params for this symbol back to null/empty */
  onReset: (symbolId: number) => void;
  /** Whether risk control blocks orders for this symbol */
  riskBlocked: boolean;
  /** Human-readable reason why orders are blocked */
  riskReason: string;
  /** Current spread in pips (null = no data) */
  spreadPips: number | null;
  /** Current open positions count for this symbol */
  positionCount: number;
  /** Current order loop status (null = no active loop) */
  orderStatus: OrderLoopStatus | null;
  /** Current off engine status (null = no active off engine) */
  offEngineStatus: OffEngineStatus | null;
}

type DropdownMode = 'orderNow' | 'direction' | null;

export default function PlayCell({
  symbolId,
  conditionPrice,
  maxOrders,
  direction,
  orderDirection,
  onPriceChange,
  onMaxChange,
  onDirectionChange,
  onOrderDirectionChange,
  orderVolume,
  onVolumeChange,
  onReset,
  riskBlocked,
  riskReason,
  spreadPips,
  positionCount,
  orderStatus,
  offEngineStatus,
}: PlayCellProps) {
  const [dropdownMode, setDropdownMode] = useState<DropdownMode>(null);
  const [showRiskTooltip, setShowRiskTooltip] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownMode(null);
        setShowRiskTooltip(false);
      }
    }
    if (dropdownMode || showRiskTooltip) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [dropdownMode, showRiskTooltip]);

  const handlePriceChange = (val: string) => {
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      onPriceChange(symbolId, val);
    }
  };

  const handleMaxChange = (val: string) => {
    if (val === '' || /^\d+$/.test(val)) {
      onMaxChange(symbolId, val);
    }
  };

  const handleVolumeChange = (val: string) => {
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      onVolumeChange(symbolId, val);
    }
  };

  // ─── Off button handlers ─────────────────────────────────

  const handleSelectDirection = (dir: 'buy' | 'sell') => {
    onDirectionChange(symbolId, dir);
    setDropdownMode(null);
  };

  const handleDirectionToggleClick = () => {
    if (riskBlocked) {
      setShowRiskTooltip(!showRiskTooltip);
      return;
    }
    if (direction) {
      onDirectionChange(symbolId, null);
    } else {
      setDropdownMode(dropdownMode === 'direction' ? null : 'direction');
    }
  };

  // ─── Order Now button handlers ───────────────────────────

  const handleSelectOrderDirection = (dir: 'buy' | 'sell') => {
    onOrderDirectionChange(symbolId, dir);
    setDropdownMode(null);
  };

  const handleOrderNowToggleClick = () => {
    if (riskBlocked) {
      setShowRiskTooltip(!showRiskTooltip);
      return;
    }
    if (orderDirection) {
      // Already BUY or SELL → toggle off (back to Order Now)
      onOrderDirectionChange(symbolId, null);
    } else {
      // Order Now → show Buy/Sell dropdown
      setDropdownMode(dropdownMode === 'orderNow' ? null : 'orderNow');
    }
  };

  // ─── Off button rendering ────────────────────────────────

  const isDirectionSet = direction !== null;

  const directionBtnStyle = direction === 'buy'
    ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25'
    : direction === 'sell'
    ? 'bg-rose-500/15 text-rose-400 border border-rose-500/30 hover:bg-rose-500/25'
    : 'bg-background-200/70 text-foreground-400 border border-background-300/60 hover:bg-background-300/70 hover:text-foreground-600';

  const directionLabel = direction === 'buy' ? 'BUY' : direction === 'sell' ? 'SELL' : 'OFF';

  const directionDotColor = direction === 'buy'
    ? 'bg-emerald-400 animate-pulse'
    : direction === 'sell'
    ? 'bg-rose-400 animate-pulse'
    : 'bg-foreground-300';

  // ─── Order Now button rendering ──────────────────────────

  const isOrderSet = orderDirection !== null;

  const orderBtnStyle = orderDirection === 'buy'
    ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25'
    : orderDirection === 'sell'
    ? 'bg-rose-500/15 text-rose-400 border border-rose-500/30 hover:bg-rose-500/25'
    : 'bg-background-200/70 text-foreground-400 border border-background-300/60 hover:bg-background-300/70 hover:text-foreground-600';

  const orderLabel = orderDirection === 'buy' ? 'BUY' : orderDirection === 'sell' ? 'SELL' : 'ORDER';

  const orderDotColor = orderDirection === 'buy'
    ? 'bg-emerald-400 animate-pulse'
    : orderDirection === 'sell'
    ? 'bg-rose-400 animate-pulse'
    : 'bg-foreground-300';

  // ─── Risk status indicator ───────────────────────────────

  const riskBlockedBySpread = riskBlocked && riskReason.toLowerCase().includes('spread');
  const riskBlockedByMax = riskBlocked && riskReason.toLowerCase().includes('max');
  const riskBlockedBySingle = riskBlocked && (
    riskReason.toLowerCase().includes('single') ||
    riskReason.toLowerCase().includes('symbol')
  );
  const riskBlockedByAfterHours = riskBlocked && riskReason.toLowerCase().includes('shutdown');

  // ─── Order status indicator ─────────────────────────────

  const ORDER_STATUS_CONFIG: Record<OrderLoopPhase, { icon: string; color: string; bg: string }> = {
    idle:             { icon: 'ri-restart-line',        color: 'text-foreground-500', bg: 'bg-background-200/60' },
    checking_risk:    { icon: 'ri-shield-check-line',   color: 'text-amber-400',      bg: 'bg-amber-500/10' },
    placing_order:    { icon: 'ri-send-plane-line',     color: 'text-amber-400',      bg: 'bg-amber-500/10' },
    waiting_confirmation: { icon: 'ri-hourglass-line',  color: 'text-accent-400',     bg: 'bg-accent-500/10' },
    completed:        { icon: 'ri-check-double-line',   color: 'text-emerald-400',    bg: 'bg-emerald-500/10' },
    max_reached:      { icon: 'ri-check-line',          color: 'text-emerald-400',    bg: 'bg-emerald-500/10' },
    blocked:          { icon: 'ri-forbid-line',         color: 'text-red-400',        bg: 'bg-red-500/10' },
    error:            { icon: 'ri-error-warning-line',  color: 'text-red-400',        bg: 'bg-red-500/10' },
  };

  const showOrderStatus = orderStatus && orderStatus.phase !== 'idle';
  const phaseConfig = orderStatus ? ORDER_STATUS_CONFIG[orderStatus.phase] : null;

  const statusMessage = orderStatus
    ? (orderStatus.lastError && orderStatus.phase === 'error'
        ? orderStatus.lastError
        : orderStatus.message)
    : '';

  // ─── Off Engine status indicator ─────────────────────────

  const OFF_ENGINE_STATUS_CONFIG: Record<OffEnginePhase, { icon: string; color: string; bg: string }> = {
    monitoring:     { icon: 'ri-radar-line',           color: 'text-accent-400',    bg: 'bg-accent-500/10' },
    auto_off:       { icon: 'ri-close-circle-line',    color: 'text-foreground-500', bg: 'bg-background-200/60' },
    triggered:      { icon: 'ri-flashlight-line',      color: 'text-amber-400',     bg: 'bg-amber-500/10' },
    ordering:       { icon: 'ri-send-plane-line',      color: 'text-amber-400',     bg: 'bg-amber-500/10' },
    completed:      { icon: 'ri-check-double-line',    color: 'text-emerald-400',   bg: 'bg-emerald-500/10' },
    blocked:        { icon: 'ri-forbid-line',          color: 'text-red-400',       bg: 'bg-red-500/10' },
    error:          { icon: 'ri-error-warning-line',   color: 'text-red-400',       bg: 'bg-red-500/10' },
  };

  const showOffEngineStatus = offEngineStatus && offEngineStatus.phase !== 'monitoring';
  const offPhaseConfig = offEngineStatus ? OFF_ENGINE_STATUS_CONFIG[offEngineStatus.phase] : null;

  const offStatusMessage = offEngineStatus?.message || '';

  // Whether this symbol has any play params set (non-default).
  // Used to highlight the Reset button so traders can spot symbols with active setup.
  const hasParams =
    conditionPrice.trim() !== '' ||
    (maxOrders !== '' && maxOrders !== '0') ||
    direction !== null ||
    orderDirection !== null;

  return (
    <div className="flex items-center gap-3 px-2 py-1.5">
      {/* Left: Params */}
      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
        {/* Condition Price */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-medium text-foreground-500 w-9 shrink-0 whitespace-nowrap">
            Price
          </span>
          <div className="relative flex-1">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-foreground-400 font-mono">
              $
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={conditionPrice}
              onChange={(e) => handlePriceChange(e.target.value)}
              placeholder="0.00"
              className="w-full pl-4 pr-2 py-1 bg-background-100 border border-background-200/70 rounded-md text-[11px] font-mono text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/10 transition-all"
            />
          </div>
        </div>

        {/* Max Orders */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-medium text-foreground-500 w-9 shrink-0 whitespace-nowrap">
            Max
          </span>
          <div className="relative flex-1">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-foreground-400 font-mono">
              #
            </span>
            <input
              type="text"
              inputMode="numeric"
              value={maxOrders}
              onChange={(e) => handleMaxChange(e.target.value)}
              placeholder="0"
              className="w-full pl-4 pr-2 py-1 bg-background-100 border border-background-200/70 rounded-md text-[11px] font-mono text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/10 transition-all"
            />
          </div>
          {/* Position count badge */}
          {positionCount > 0 && (
            <span className="text-[9px] font-mono font-semibold text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded whitespace-nowrap">
              {positionCount} pos
            </span>
          )}
        </div>

        {/* Volume (for Order engine) */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-medium text-foreground-500 w-9 shrink-0 whitespace-nowrap">
            Vol
          </span>
          <div className="relative flex-1">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-foreground-400 font-mono">
              L
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={orderVolume}
              onChange={(e) => handleVolumeChange(e.target.value)}
              placeholder="0.01"
              className="w-full pl-4 pr-2 py-1 bg-background-100 border border-background-200/70 rounded-md text-[11px] font-mono text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/10 transition-all"
            />
          </div>
        </div>

        {/* Spread indicator */}
        {spreadPips !== null && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-medium text-foreground-500 w-9 shrink-0 whitespace-nowrap">
              Spread
            </span>
            <span className={`text-[10px] font-mono font-semibold ${spreadPips > 3 ? 'text-red-400' : 'text-foreground-600'}`}>
              {spreadPips.toFixed(1)}p
            </span>
          </div>
        )}
      </div>

      {/* Right: Action Buttons */}
      <div className="flex flex-col gap-1.5 shrink-0" ref={dropdownRef}>
        {/* ─── Order Now Toggle: ORDER → Buy/Sell → BUY/SELL ─── */}
        <div className="relative">
          <button
            type="button"
            onClick={handleOrderNowToggleClick}
            className={`
              relative inline-flex items-center rounded-md transition-all duration-200 cursor-pointer
              min-w-[72px] justify-center px-3 py-1 text-[10px] font-semibold whitespace-nowrap
              ${riskBlocked
                ? 'bg-foreground-200/60 text-foreground-400 cursor-not-allowed'
                : orderBtnStyle
              }
            `}
            title={riskBlocked ? riskReason : isOrderSet ? `Cancel order (${orderLabel})` : 'Send market order'}
            disabled={riskBlocked}
          >
            {riskBlocked ? (
              <>
                <i className="ri-shield-cross-line text-[11px] mr-1.5"></i>
                Blocked
              </>
            ) : (
              <>
                <span className={`w-1.5 h-1.5 rounded-full mr-1.5 transition-colors ${orderDotColor}`}></span>
                {orderLabel}
                {!isOrderSet && (
                  <i className={`ri-arrow-down-s-line text-[10px] ml-0.5 transition-transform ${dropdownMode === 'orderNow' ? 'rotate-180' : ''}`}></i>
                )}
              </>
            )}
          </button>

          {/* Order Now → Buy/Sell dropdown */}
          {dropdownMode === 'orderNow' && !riskBlocked && !orderDirection && (
            <div className="absolute top-full mt-1 left-0 right-0 bg-background-50 border border-background-200/70 rounded-lg shadow-lg z-50 overflow-hidden">
              <button
                type="button"
                onClick={() => handleSelectOrderDirection('buy')}
                className="w-full text-left px-3 py-2 text-[11px] font-semibold transition-colors cursor-pointer flex items-center gap-2 hover:bg-emerald-500/10 text-emerald-400"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0"></span>
                Buy
              </button>
              <button
                type="button"
                onClick={() => handleSelectOrderDirection('sell')}
                className="w-full text-left px-3 py-2 text-[11px] font-semibold transition-colors cursor-pointer flex items-center gap-2 hover:bg-rose-500/10 text-rose-400"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400 shrink-0"></span>
                Sell
              </button>
            </div>
          )}

          {/* Risk tooltip */}
          {riskBlocked && showRiskTooltip && (
            <div className="absolute top-full mt-1 left-0 right-0 bg-red-500/10 border border-red-500/20 rounded-lg p-2 shadow-lg z-50 min-w-[180px]">
              <div className="flex items-start gap-1.5">
                <i className="ri-error-warning-line text-red-400 text-[11px] mt-0.5 shrink-0"></i>
                <p className="text-[10px] text-red-400 leading-relaxed">{riskReason}</p>
              </div>
              <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                {riskBlockedBySpread && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 text-[9px] font-medium">
                    <i className="ri-pulse-line"></i>
                    Spread &gt; 3p
                  </span>
                )}
                {riskBlockedByMax && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 text-[9px] font-medium">
                    <i className="ri-stack-line"></i>
                    Max Orders
                  </span>
                )}
                {riskBlockedBySingle && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 text-[9px] font-medium">
                    <i className="ri-forbid-line"></i>
                    Single Symbol
                  </span>
                )}
                {riskBlockedByAfterHours && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 text-[9px] font-medium">
                    <i className="ri-moon-line"></i>
                    After 23:00
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ─── Order Status Indicator ─── */}
        {showOrderStatus && phaseConfig && (
          <div
            className={`flex items-center gap-1.5 px-2 py-1 rounded-md ${phaseConfig.bg} ${phaseConfig.color} text-[10px] font-medium transition-all`}
            title={statusMessage}
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
              <i className={`${phaseConfig.icon} text-[11px] ${orderStatus!.phase === 'waiting_confirmation' ? 'animate-spin' : ''}`}></i>
            </div>
            <span className="truncate max-w-[130px] leading-tight">
              {statusMessage || orderStatus!.phase.replace('_', ' ')}
            </span>
            {orderStatus!.ordersPlaced > 0 && (
              <span className="text-[9px] opacity-70 font-mono shrink-0 ml-auto">
                {orderStatus!.ordersPlaced}/{orderStatus!.maxOrders}
              </span>
            )}
          </div>
        )}

        {/* ─── Off Engine Status Indicator ─── */}
        {showOffEngineStatus && offPhaseConfig && (
          <div
            className={`flex items-center gap-1.5 px-2 py-1 rounded-md ${offPhaseConfig.bg} ${offPhaseConfig.color} text-[10px] font-medium transition-all`}
            title={offStatusMessage}
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
              <i className={`${offPhaseConfig.icon} text-[11px] ${offEngineStatus!.phase === 'monitoring' ? 'animate-pulse' : ''}`}></i>
            </div>
            <span className="truncate max-w-[130px] leading-tight">
              {offStatusMessage || offEngineStatus!.phase.replace('_', ' ')}
            </span>
            {offEngineStatus!.ordersPlaced > 0 && (
              <span className="text-[9px] opacity-70 font-mono shrink-0 ml-auto">
                {offEngineStatus!.ordersPlaced}/{offEngineStatus!.maxOrders}
              </span>
            )}
          </div>
        )}

        {/* ─── Off Toggle: OFF → Buy/Sell → BUY/SELL ─── */}
        <div className="relative">
          <button
            type="button"
            onClick={handleDirectionToggleClick}
            className={`
              relative inline-flex items-center rounded-md transition-all duration-200 cursor-pointer
              min-w-[72px] justify-center px-3 py-1 text-[10px] font-semibold whitespace-nowrap
              ${riskBlocked
                ? 'bg-foreground-200/60 text-foreground-400 cursor-not-allowed'
                : directionBtnStyle
              }
            `}
            title={riskBlocked ? riskReason : isDirectionSet ? `Stop monitoring (${directionLabel})` : 'Start monitoring'}
            disabled={riskBlocked}
          >
            {riskBlocked ? (
              <>
                <i className="ri-shield-cross-line text-[11px] mr-1.5"></i>
                Blocked
              </>
            ) : (
              <>
                <span className={`w-1.5 h-1.5 rounded-full mr-1.5 transition-colors ${directionDotColor}`}></span>
                {directionLabel}
                {!isDirectionSet && (
                  <i className={`ri-arrow-down-s-line text-[10px] ml-0.5 transition-transform ${dropdownMode === 'direction' ? 'rotate-180' : ''}`}></i>
                )}
              </>
            )}
          </button>

          {/* OFF → Buy/Sell dropdown */}
          {dropdownMode === 'direction' && !riskBlocked && (
            <div className="absolute top-full mt-1 left-0 right-0 bg-background-50 border border-background-200/70 rounded-lg shadow-lg z-50 overflow-hidden">
              <button
                type="button"
                onClick={() => handleSelectDirection('buy')}
                className="w-full text-left px-3 py-2 text-[11px] font-semibold transition-colors cursor-pointer flex items-center gap-2 hover:bg-emerald-500/10 text-emerald-400"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0"></span>
                Buy
              </button>
              <button
                type="button"
                onClick={() => handleSelectDirection('sell')}
                className="w-full text-left px-3 py-2 text-[11px] font-semibold transition-colors cursor-pointer flex items-center gap-2 hover:bg-rose-500/10 text-rose-400"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400 shrink-0"></span>
                Sell
              </button>
            </div>
          )}
        </div>

        {/* ─── Reset Params: clear all play params back to null ─── */}
        <button
          type="button"
          onClick={() => onReset(symbolId)}
          className={`inline-flex items-center justify-center rounded-md min-w-[72px] px-3 py-1 text-[10px] font-semibold whitespace-nowrap border transition-all cursor-pointer ${
            hasParams
              ? 'bg-accent-500/15 text-accent-400 border-accent-500/40 hover:bg-accent-500/25'
              : 'bg-background-200/70 text-foreground-500 border-background-300/60 hover:bg-background-300/70 hover:text-foreground-700'
          }`}
          title={hasParams ? 'Reset play params to null (keeps volume)' : 'No params set — nothing to reset'}
        >
          <span className={`w-1.5 h-1.5 rounded-full mr-1.5 shrink-0 ${hasParams ? 'bg-accent-400' : 'bg-foreground-300'}`}></span>
          <i className="ri-refresh-line text-[11px] mr-1"></i>
          Reset
        </button>
      </div>
    </div>
  );
}